import { useCallback, useRef } from 'react';

/**
 * Custom hook that debounces a function execution
 * @param {Function} func - The function to execute after delay
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} The debounced function
 */
export const useDebounce = (func, delay) => {
  const timeoutRef = useRef(null);

  const debouncedFunction = useCallback(
    (...args) => {
      // Clear the previous timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set up a new timeout to execute the function after the delay
      timeoutRef.current = setTimeout(() => {
        func(...args);
      }, delay);
    },
    [func, delay]
  );

  // Cleanup function to clear timeout on unmount
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Expose both the debounced function and a cancel method
  debouncedFunction.cancel = cancel;

  return debouncedFunction;
};
